import { useEffect, useMemo, useState } from "react";
import api, { formatError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { tabAccessibleFor } from "../lib/modules";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Plus, Eye, CheckCircle2, XCircle, Wallet, Settings2, Search, Paperclip, Upload, Download, Trash } from "lucide-react";
import { PageHeader } from "../components/GradientCard";
import { toast } from "sonner";
import { useGlobalViewMode } from "../components/ViewModeToggle";
import { PurchaseLine, VendorSearchPicker } from "../components/PurchaseLine";
import DeleteWithReasonDialog from "../components/DeleteWithReasonDialog";

const EMPTY_PURCHASE_LINE = { inventory_id: "", inventory_label: "", custom_name: "", qty_purchased: 1, purchase_price: 0 };

const STATUS_COLOR = {
  Submitted: "bg-amber-100 text-amber-800",
  PendingApproval: "bg-amber-100 text-amber-800",
  Resubmitted: "bg-sky-100 text-sky-800",
  RejectedByApprover: "bg-rose-100 text-rose-800",
  PendingReimbursement: "bg-indigo-100 text-indigo-800",
  Pending: "bg-orange-100 text-orange-800",
  Paid: "bg-emerald-100 text-emerald-800",
};
const STATUS_FILTERS = ["All", "PendingApproval", "RejectedByApprover", "PendingReimbursement", "Paid"];

function emptyForm(user) {
  return {
    expense_date: new Date().toISOString().slice(0, 10),
    categories: [],
    amount: "",
    purpose: "",
    remark: "",
    paid_by_employee_id: user?.id || "",
    paid_by_employee_name: user?.name || "",
    payment_mode_used: "Cash",
    // Purchase-specific (only used when "Purchase" category is selected)
    purchase_vendor_id: "",
    purchase_new_vendor_name: "",
    purchase_lines: [],
    purchase_notes: "",
  };
}

function matchesFilter(e, q, status) {
  if (status && status !== "All" && e.status !== status) return false;
  if (!q) return true;
  const hay = [
    e.created_by_name, e.created_by_department, e.created_by_employee_code,
    e.category, (e.categories || []).join(" "),
    e.purpose, e.remark, e.approval_remark, e.rejection_remark,
    e.payment_mode, e.reference_number, e.cash_remark,
    e.paid_by_employee_name, e.payment_mode_used,
  ].filter(Boolean).join(" ").toLowerCase();
  return hay.includes(q.toLowerCase());
}

export default function DailyExpense() {
  const { user } = useAuth();
  const [tab, setTab] = useState("submit");
  const [access, setAccess] = useState({ i_can_approve: false, i_can_cash: false, is_master: false, approval_user_ids: [], cash_user_ids: [], categories: [], payment_modes: [] });
  const [people, setPeople] = useState([]);
  const [mine, setMine] = useState([]);
  const [reimb, setReimb] = useState([]);
  const [disbursements, setDisbursements] = useState([]);
  const [cashbookSummary, setCashbookSummary] = useState([]);
  const [cashbookDetail, setCashbookDetail] = useState(null);  // {employee_id|employee_name, ...book}
  const [externalNames, setExternalNames] = useState([]);
  const [cashInHand, setCashInHand] = useState(null);
  const [filterText, setFilterText] = useState("");
  const [filterStatus, setFilterStatus] = useState("All");
  const [openCreate, setOpenCreate] = useState(false);
  const [form, setForm] = useState(() => emptyForm(user));
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);
  // v109.9.40 — Tab Access dialog removed; state kept only as a stub to avoid rippling changes.
  const tabAccessOpen = false; const setTabAccessOpen = () => {};
  void tabAccessOpen; void setTabAccessOpen;
  const [openCashAdd, setOpenCashAdd] = useState(false);
  const [openReimbPay, setOpenReimbPay] = useState(null); // expense being paid
  const [billFile, setBillFile] = useState(null);
  const [inventory] = useState([]);
  const [vendors, setVendors] = useState([]);
  const isPurchase = (form.categories || []).includes("Purchase");

  const loadAccess = async () => { try { const { data } = await api.get("/daily-expenses/tab-access"); setAccess(data); } catch { /* */ } };
  const loadMine = async () => { try { const { data } = await api.get("/daily-expenses?scope=mine"); setMine(data || []); } catch { /* */ } };
  const loadReimb = async () => { if (!access.i_can_approve) return; try { const { data } = await api.get("/daily-expenses?scope=reimbursement"); setReimb(data || []); } catch { /* */ } };
  // v109.9.168 — Approver queue (single global approver picked by MA at top)
  const [approvalRows, setApprovalRows] = useState([]);
  const loadApproval = async () => {
    if (!access.i_am_approver) return;
    try { const { data } = await api.get("/daily-expenses?scope=approval"); setApprovalRows(data || []); } catch { /* */ }
  };
  const loadDisbursements = async () => {
    try {
      const scope = access.i_can_cash ? "all" : "mine";
      const { data } = await api.get(`/cash-disbursements?scope=${scope}`);
      setDisbursements(data || []);
    } catch { /* */ }
  };
  const loadCashInHand = async () => {
    try { const { data } = await api.get("/cash-disbursements/cash-in-hand"); setCashInHand(data); }
    catch { /* */ }
  };
  const loadCashbookSummary = async () => {
    if (!access.i_can_cash) return;
    try { const { data } = await api.get("/cash-disbursements/cashbook-summary"); setCashbookSummary(data || []); }
    catch { /* */ }
  };
  const loadExternalNames = async () => {
    if (!access.i_can_cash) return;
    try { const { data } = await api.get("/cash-disbursements/external-names"); setExternalNames(data || []); }
    catch { /* */ }
  };
  const openCashbookFor = async (row) => {
    try {
      const params = row.employee_id ? `employee_id=${row.employee_id}` : `employee_name=${encodeURIComponent(row.employee_name)}`;
      const { data } = await api.get(`/cash-disbursements/cashbook?${params}`);
      setCashbookDetail(data);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  useEffect(() => {
    loadAccess();
    loadCashInHand();
    api.get("/directory").then(({ data }) => setPeople(data || [])).catch(() => {});
    api.get("/vendors").then(({ data }) => setVendors(data || [])).catch(() => {});
  }, []); // eslint-disable-line

  useEffect(() => {
    if (tab === "submit") { loadMine(); loadCashInHand(); }
    if (tab === "approval") loadApproval();
    if (tab === "reimbursement") loadReimb();
    if (tab === "cash") { loadDisbursements(); loadExternalNames(); }
    if (tab === "cashbook") { loadCashbookSummary(); loadExternalNames(); }
    setFilterText(""); setFilterStatus("All");
  }, [tab, access.i_can_approve, access.i_can_cash, access.i_am_approver]); // eslint-disable-line

  const filteredMine = useMemo(() => mine.filter((e) => matchesFilter(e, filterText, filterStatus)), [mine, filterText, filterStatus]);
  const filteredReimb = useMemo(() => reimb.filter((e) => matchesFilter(e, filterText, filterStatus)), [reimb, filterText, filterStatus]);

  // Re-sync booker fields when user becomes available (mount-vs-auth race)
  useEffect(() => {
    if (!user || editing) return;
    setForm((f) => ({
      ...f,
      paid_by_employee_id: f.paid_by_employee_id || user.id || "",
      paid_by_employee_name: f.paid_by_employee_name || user.name || "",
    }));
  }, [user?.id, user?.name]); // eslint-disable-line

  const openNew = () => { setEditing(null); setForm(emptyForm(user)); setBillFile(null); setOpenCreate(true); };
  const openEdit = (e) => {
    setEditing(e);
    setForm({
      expense_date: e.expense_date,
      categories: e.categories && e.categories.length ? e.categories : (e.category ? [e.category] : []),
      amount: e.amount,
      purpose: e.purpose,
      remark: e.remark || "",
      paid_by_employee_id: e.paid_by_employee_id || "",
      paid_by_employee_name: e.paid_by_employee_name || "",
      payment_mode_used: e.payment_mode_used || "Cash",
      purchase_vendor_id: "",
      purchase_new_vendor_name: "",
      purchase_lines: [],
      purchase_notes: "",
    });
    setBillFile(null);
    setOpenCreate(true);
  };

  const submit = async () => {
    if (!form.expense_date) { toast.error("Expense date is required"); return; }
    if (!form.categories || form.categories.length === 0) { toast.error("Pick at least one category"); return; }
    if (!form.purpose?.trim()) { toast.error("Breakup of Expense is required"); return; }
    if (!form.paid_by_employee_id) { toast.error("Pick who paid for this"); return; }
    if (!form.payment_mode_used) { toast.error("Pick payment mode"); return; }
    const isPurchaseSel = (form.categories || []).includes("Purchase");
    // Validate purchase block first when applicable
    if (isPurchaseSel) {
      if (!form.purchase_vendor_id && !(form.purchase_new_vendor_name || "").trim()) {
        toast.error("Pick or add a vendor for the Purchase"); return;
      }
      const validLines = (form.purchase_lines || []).filter((l) => (l.inventory_id || (l.custom_name || "").trim()) && Number(l.qty_purchased) > 0);
      if (validLines.length === 0) { toast.error("Add at least one Purchase line"); return; }
    }
    if (!isPurchaseSel) {
      if (!form.amount || Number(form.amount) <= 0) { toast.error("Amount must be greater than 0"); return; }
    }
    try {
      let purchase = null;
      // Step 1 — when Purchase category is selected: create a Purchase record
      // first (same backend route, same rules — items added/updated in Inventory)
      if (isPurchaseSel) {
        const purchaseBody = {
          vendor_id: form.purchase_vendor_id || "",
          new_vendor_name: form.purchase_vendor_id ? "" : (form.purchase_new_vendor_name || "").trim(),
          items: form.purchase_lines
            .filter((l) => (l.inventory_id || (l.custom_name || "").trim()) && Number(l.qty_purchased) > 0)
            .map((l) => ({
              inventory_id: l.inventory_id || "",
              custom_name: l.inventory_id ? "" : (l.custom_name || "").trim(),
              qty_purchased: Number(l.qty_purchased),
              purchase_price: Number(l.purchase_price || 0),
            })),
          notes: form.purchase_notes || "",
        };
        const { data: p } = await api.post("/purchases", purchaseBody);
        purchase = p;
      }
      const expenseBody = {
        expense_date: form.expense_date,
        categories: form.categories,
        amount: isPurchaseSel ? (purchase?.total_amount ?? 0) : Number(form.amount),
        purpose: form.purpose,
        remark: form.remark,
        paid_by_employee_id: form.paid_by_employee_id,
        paid_by_employee_name: form.paid_by_employee_name,
        payment_mode_used: form.payment_mode_used,
        purchase_id: purchase?.id || "",
        purchase_no: purchase?.purchase_no || "",
      };
      let expenseId;
      if (editing) {
        const { data } = await api.patch(`/daily-expenses/${editing.id}`, expenseBody);
        expenseId = data.id;
        toast.success("Expense updated");
      } else {
        const { data } = await api.post("/daily-expenses", expenseBody);
        expenseId = data.id;
        toast.success(purchase ? `Expense submitted · Purchase ${purchase.purchase_no} recorded` : "Expense submitted");
      }
      // Step 2 — upload the bill if user picked one
      if (billFile && expenseId) {
        const fd = new FormData(); fd.append("file", billFile);
        try {
          await api.post(`/daily-expenses/${expenseId}/receipt`, fd, { headers: { "Content-Type": "multipart/form-data" } });
          toast.success("Bill uploaded");
        } catch (er) { toast.error(`Bill upload failed: ${formatError(er.response?.data?.detail)}`); }
      }
      setOpenCreate(false); setEditing(null); setForm(emptyForm(user)); setBillFile(null); loadMine();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const markPending = async (e) => {
    const remark = window.prompt("Pending remark (optional):", "") || "";
    try {
      await api.post(`/daily-expenses/${e.id}/reimbursement-status`, { action: "pending", remark });
      toast.success("Marked Pending");
      loadReimb();
    } catch (er) { toast.error(formatError(er.response?.data?.detail)); }
  };

  const [pendingDeleteExpense, setPendingDeleteExpense] = useState(null);
  const deleteExpense = (e) => setPendingDeleteExpense(e);
  const confirmDeleteExpense = async (reason) => {
    try {
      await api.delete(`/daily-expenses/${pendingDeleteExpense.id}`, { data: { reason } });
      toast.success("Expense moved to Recycle Bin");
      loadMine(); loadReimb();
    } catch (er) { toast.error(formatError(er.response?.data?.detail)); throw er; }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        moduleKey="daily_expense"
        tone="amber"
        icon={Wallet}
        eyebrow="Accounts"
        title="Daily Expense"
        subtitle="Submit your daily expenses, get reimbursed, and track cash in hand."
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            {cashInHand && (
              <div className="px-3 py-1.5 rounded-full border border-emerald-200 bg-white/80 text-[11.5px]" data-testid="de-cih-widget">
                <span className="uppercase tracking-wider font-semibold text-emerald-700">Cash in Hand</span>
                <span className="ml-2 mono font-bold text-emerald-900">₹{cashInHand.cash_in_hand?.toLocaleString("en-IN") || 0}</span>
              </div>
            )}
            <Button onClick={openNew} className="bg-gradient-to-br from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white shadow-sm" data-testid="de-new">
              <Plus size={14} className="mr-1.5" /> New Expense
            </Button>
          </div>
        }
      />

      {/* v109.9.168 — Global Approver picker (MA only, top of module) */}
      {access.is_master && (
        <div className="pk-card p-3 mt-2 mb-3 flex items-center gap-3 flex-wrap" data-testid="de-approver-picker">
          <span className="text-[11px] font-semibold text-slate-700 uppercase tracking-wider">Global Approver for Daily Expenses</span>
          <select
            className="h-8 rounded-md border border-slate-300 bg-white px-2 text-[12px] min-w-[220px]"
            value={access.approver_user_id || ""}
            onChange={async (e) => {
              const uid = e.target.value;
              try {
                await api.post("/daily-expenses/tab-access", { approval_user_ids: access.approval_user_ids || [], cash_user_ids: access.cash_user_ids || [], approver_user_id: uid });
                toast.success(uid ? "Approver updated" : "Approver cleared");
                loadAccess();
              } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
            }}
            data-testid="de-approver-select"
          >
            <option value="">— Pick an employee —</option>
            {people.map((p) => (
              <option key={p.id} value={p.id}>{p.name} · {p.email}</option>
            ))}
          </select>
          {access.approver_user_name && (
            <span className="text-[10.5px] text-emerald-700 bg-emerald-50 ring-1 ring-emerald-200 px-2 py-0.5 rounded-full">
              Current: <b>{access.approver_user_name}</b>
            </span>
          )}
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          {tabAccessibleFor(user, "daily_expense", "submit") && (
            <TabsTrigger value="submit" data-testid="de-tab-submit">Submit Your Expense ({mine.length})</TabsTrigger>
          )}
          {/* v109.9.168 — Approval tab visible ONLY to the configured global approver (or MA) */}
          {access.i_am_approver && <TabsTrigger value="approval" data-testid="de-tab-approval">Approval ({approvalRows.length})</TabsTrigger>}
          {access.i_can_approve && tabAccessibleFor(user, "daily_expense", "reimbursement") && <TabsTrigger value="reimbursement" data-testid="de-tab-reimbursement">Reimbursement ({reimb.length})</TabsTrigger>}
          {tabAccessibleFor(user, "daily_expense", "cash") && (
            <TabsTrigger value="cash" data-testid="de-tab-cash">Cash Given ({disbursements.length})</TabsTrigger>
          )}
          {access.i_can_cash && tabAccessibleFor(user, "daily_expense", "cashbook") && <TabsTrigger value="cashbook" data-testid="de-tab-cashbook">Cash Book</TabsTrigger>}
        </TabsList>

        <FilterBar q={filterText} setQ={setFilterText} status={filterStatus} setStatus={setFilterStatus} />

        <TabsContent value="submit" className="mt-4">
          <ExpenseTable
            rows={filteredMine} emptyText="No expenses yet."
            onView={setViewing}
            isMaster={access.is_master}
            onEdit={(e) => {
              if (access.is_master) return openEdit(e);
              if (e.status === "RejectedByApprover" && String(e.created_by_id) === String(user?.id)) return openEdit(e);
            }}
            canEdit={(e) => access.is_master || (e.status === "RejectedByApprover" && String(e.created_by_id) === String(user?.id))}
            onDelete={access.is_master ? deleteExpense : null}
            onResubmit={async (e) => {
              const c = window.prompt(
                `Rejected reason from approver:\n"${e.rejection_reason || "—"}"\n\nAdd a clarification and resubmit (required):`,
                ""
              );
              if (!c || !c.trim()) return;
              try {
                await api.post(`/daily-expenses/${e.id}/resubmit`, { clarification: c.trim() });
                toast.success("Resubmitted — approver will see it again");
                loadMine();
              } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
            }}
          />
        </TabsContent>
        {/* v109.9.168 — Approval tab (approver only). Approve → Reimbursement; Reject → back to submitter with reason. */}
        {access.i_am_approver && (
          <TabsContent value="approval" className="mt-4">
            <ApprovalQueueTable
              rows={approvalRows}
              currentUserId={user?.id}
              onView={setViewing}
              onApprove={async (e) => {
                if (!window.confirm(`Approve this expense (₹${e.amount}) — it will move to Reimbursement.`)) return;
                try { await api.post(`/daily-expenses/${e.id}/approve`, {}); toast.success("Approved"); loadApproval(); loadReimb(); }
                catch (err) { toast.error(formatError(err.response?.data?.detail)); }
              }}
              onReject={async (e) => {
                const r = window.prompt("Rejection reason (visible to submitter):", "");
                if (!r || !r.trim()) return;
                try { await api.post(`/daily-expenses/${e.id}/reject-to-submitter`, { reason: r.trim() }); toast.success("Sent back for clarification"); loadApproval(); }
                catch (err) { toast.error(formatError(err.response?.data?.detail)); }
              }}
            />
          </TabsContent>
        )}
        {access.i_can_approve && (
          <TabsContent value="reimbursement" className="mt-4">
            <ExpenseTable
              rows={filteredReimb} emptyText="No expenses awaiting reimbursement."
              onView={setViewing}
              isMaster={access.is_master}
              onMarkPaid={(e) => setOpenReimbPay(e)}
              onMarkPending={markPending}
              onDelete={access.is_master ? deleteExpense : null}
            />
          </TabsContent>
        )}
        <TabsContent value="cash" className="mt-4">
          <CashGivenTable
            rows={disbursements} canAdd={access.i_can_cash}
            currentUserId={user?.id} isMaster={access.is_master}
            onAdd={() => setOpenCashAdd(true)}
            onChanged={() => { loadDisbursements(); loadCashInHand(); }}
          />
        </TabsContent>
        {access.i_can_cash && (
          <TabsContent value="cashbook" className="mt-4">
            <CashBookTab summary={cashbookSummary} onOpen={openCashbookFor} />
          </TabsContent>
        )}
      </Tabs>

      <CashBookDialog
        detail={cashbookDetail}
        onClose={() => setCashbookDetail(null)}
        canAddDebit={access.i_can_cash}
        paymentModes={access.payment_modes || []}
        onDebitAdded={() => { openCashbookFor({ employee_name: cashbookDetail?.employee_name, employee_id: cashbookDetail?.employee_id }); loadCashbookSummary(); loadDisbursements(); }}
      />

      <CreateDialog open={openCreate} setOpen={setOpenCreate} form={form} setForm={setForm} submit={submit} editing={editing} categories={access.categories || []} paymentModes={access.payment_modes || []} people={people} user={user} billFile={billFile} setBillFile={setBillFile} inventory={inventory} vendors={vendors} />
      <ViewDialog viewing={viewing} setViewing={setViewing} onChanged={() => { loadMine(); loadReimb(); loadDisbursements(); loadCashInHand(); }} />
      <CashAddDialog open={openCashAdd} setOpen={setOpenCashAdd} people={people} paymentModes={access.payment_modes || []} externalNames={externalNames} onSaved={() => { loadDisbursements(); loadCashInHand(); loadExternalNames(); }} />

      <DeleteWithReasonDialog
        open={!!pendingDeleteExpense}
        onClose={() => setPendingDeleteExpense(null)}
        title={pendingDeleteExpense ? `Delete expense "${(pendingDeleteExpense.purpose || "").slice(0, 40)}"?` : "Delete expense?"}
        description={pendingDeleteExpense ? `₹${pendingDeleteExpense.amount} will be moved to Admin Hub → Recycle Bin. Master Admin can restore it.` : ""}
        onConfirm={confirmDeleteExpense}
        testid="de-delete-expense"
      />
      <ReimbPayDialog viewing={openReimbPay} setViewing={setOpenReimbPay} paymentModes={access.payment_modes || []} onSaved={() => { loadReimb(); loadCashInHand(); }} />
      {access.is_master && false && <div data-testid="de-tab-access-removed" />}
    </div>
  );
}

function FilterBar({ q, setQ, status, setStatus }) {
  return (
    <div className="flex items-center gap-2 mt-3 flex-wrap" data-testid="de-filterbar">
      <div className="relative flex-1 min-w-[220px] max-w-md">
        <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search employee, category, purpose, reference…" className="pl-7 h-9 text-[12px]" data-testid="de-filter-search" />
      </div>
      <div className="flex items-center gap-1.5 text-[11px]">
        <Label className="uppercase tracking-wider font-semibold text-slate-500">Status</Label>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="h-9 border border-slate-200 rounded-md px-2 text-[12px] bg-white" data-testid="de-filter-status">
          {STATUS_FILTERS.map((o) => <option key={o}>{o}</option>)}
        </select>
        {(q || (status && status !== "All")) && (
          <Button variant="outline" onClick={() => { setQ(""); setStatus("All"); }} className="h-9 text-[11px]" data-testid="de-filter-clear">Clear</Button>
        )}
      </div>
    </div>
  );
}

function ExpenseTable({ rows, emptyText, onView, onEdit, onMarkPaid, onMarkPending, onDelete, onResubmit, canEdit }) {
  const [viewMode] = useGlobalViewMode();
  if (rows.length === 0) return <div className="pk-card p-12 text-center text-slate-400 text-sm">{emptyText}</div>;
  if (viewMode === "card") {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="de-cards">
        {rows.map((e) => (
          <div key={e.id} className="pk-card p-4 hover:shadow-md transition-shadow" data-testid={`de-card-${e.id}`} data-highlight-id={e.id}>
            <div className="flex items-start justify-between gap-2 mb-2">
              <div className="min-w-0 flex-1">
                <div className="font-bold text-slate-900 truncate">{e.created_by_name}</div>
                <div className="text-[10.5px] text-slate-500 truncate">{e.created_by_employee_code || e.created_by_department}</div>
              </div>
              <div className="text-right shrink-0">
                <Badge className={STATUS_COLOR[e.status] || "bg-slate-100"}>{e.status}</Badge>
                <div className="mono text-[12.5px] font-bold text-slate-900 mt-1">₹{e.amount}</div>
              </div>
            </div>
            {(e.categories?.length ? e.categories : [e.category].filter(Boolean)).length > 0 && (
              <div className="flex flex-wrap gap-1 mb-2">
                {(e.categories?.length ? e.categories : [e.category]).map((c, i) => <Badge key={i} variant="outline" className="text-[10px] bg-slate-50">{c}</Badge>)}
              </div>
            )}
            <div className="grid grid-cols-2 gap-2 text-[11.5px] mb-2">
              <div><div className="text-[9.5px] uppercase tracking-wider text-slate-500 font-semibold">Date</div><div className="mono text-slate-800">{e.expense_date}</div></div>
              <div><div className="text-[9.5px] uppercase tracking-wider text-slate-500 font-semibold">Paid by</div><div className="text-slate-800">{e.paid_by_employee_name || "—"}</div></div>
              <div className="col-span-2"><div className="text-[9.5px] uppercase tracking-wider text-slate-500 font-semibold">Payment</div><div className="text-slate-700">via {e.payment_mode_used || "—"}{e.status === "Paid" && e.payment_mode ? ` · ${e.payment_mode}${e.reference_number ? ` · ${e.reference_number}` : ""}` : ""}</div></div>
              {e.receipt_filename && <div className="col-span-2 text-[10.5px] text-slate-500 flex items-center gap-1"><Paperclip size={10} /> {e.receipt_filename}</div>}
            </div>
            <div className="flex flex-wrap gap-1 pt-2 border-t border-slate-100">
              <Button size="sm" variant="outline" onClick={() => onView?.(e)} className="h-7 text-[10.5px]" data-testid={`de-view-${e.id}`}><Eye size={10} className="mr-1" /> View</Button>
              {onEdit && (!canEdit || canEdit(e)) && <Button size="sm" variant="outline" onClick={() => onEdit(e)} className="h-7 text-[10.5px]" data-testid={`de-edit-${e.id}`}>Edit</Button>}
              {onMarkPaid && e.status !== "Paid" && <Button size="sm" onClick={() => onMarkPaid(e)} className="h-7 text-[10.5px] bg-emerald-600 text-white" data-testid={`de-paid-${e.id}`}><CheckCircle2 size={10} className="mr-1" /> Paid</Button>}
              {onMarkPending && e.status !== "Pending" && <Button size="sm" onClick={() => onMarkPending(e)} className="h-7 text-[10.5px] bg-orange-500 text-white" data-testid={`de-pending-${e.id}`}>Pending</Button>}
              {onResubmit && e.status === "RejectedByApprover" && <Button size="sm" onClick={() => onResubmit(e)} className="h-7 text-[10.5px] bg-sky-600 hover:bg-sky-700 text-white" data-testid={`de-resubmit-${e.id}`}>Resubmit</Button>}
              {onDelete && <Button size="sm" variant="outline" onClick={() => onDelete(e)} className="h-7 text-[10.5px] text-rose-600 border-rose-200 hover:bg-rose-50" data-testid={`de-del-${e.id}`}><Trash size={10} /></Button>}
            </div>
          </div>
        ))}
      </div>
    );
  }
  return (
    <div className="pk-card overflow-x-auto">
      <table className="w-full text-sm responsive-table">
        <thead className="border-b border-slate-200 bg-slate-50/60">
          <tr>
            <Th>Employee</Th><Th>Date</Th><Th>Category</Th><Th>Amount</Th><Th>Paid By · Via</Th><Th>Status</Th><Th className="text-right">Action</Th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={7} className="text-center py-12 text-slate-400 text-sm">{emptyText}</td></tr>}
          {rows.map((e) => (
            <tr key={e.id} className="border-b border-slate-100" data-testid={`de-row-${e.id}`} data-highlight-id={e.id}>
              <td className="px-4 py-3" data-label="Employee">
                <div className="font-semibold">{e.created_by_name}</div>
                <div className="text-[10.5px] text-slate-500">{e.created_by_employee_code || e.created_by_department}</div>
              </td>
              <td className="px-4 py-3 mono text-[12px]" data-label="Date">{e.expense_date}</td>
              <td className="px-4 py-3 text-[12px]" data-label="Category">
                {(e.categories && e.categories.length) ? (
                  <div className="flex flex-wrap gap-1">{e.categories.map((c, i) => <Badge key={i} variant="outline" className="text-[10.5px] bg-slate-50">{c}</Badge>)}</div>
                ) : (e.category || "—")}
              </td>
              <td className="px-4 py-3 mono text-[12.5px] font-semibold" data-label="Amount">₹{e.amount}</td>
              <td className="px-4 py-3 text-[12px]" data-label="Paid By">
                <div className="font-semibold">{e.paid_by_employee_name || "—"}</div>
                <div className="text-[10.5px] text-slate-500">via {e.payment_mode_used || "—"}</div>
                {e.receipt_filename && <div className="text-[10.5px] text-slate-500 flex items-center gap-1 mt-0.5"><Paperclip size={9} /> {e.receipt_filename}</div>}
              </td>
              <td className="px-4 py-3" data-label="Status">
                <Badge className={STATUS_COLOR[e.status] || "bg-slate-100"}>{e.status}</Badge>
                {e.status === "Paid" && e.payment_mode && (
                  <div className="text-[10.5px] text-slate-500 mt-0.5">{e.payment_mode}{e.reference_number ? ` · ${e.reference_number}` : ""}</div>
                )}
              </td>
              <td className="px-4 py-3 text-right whitespace-nowrap" data-label="Action">
                <Button size="sm" variant="outline" onClick={() => onView?.(e)} className="h-8 text-[11px] mr-1" data-testid={`de-view-${e.id}`}><Eye size={11} className="mr-1" /> View</Button>
                {onEdit && (!canEdit || canEdit(e)) && <Button size="sm" variant="outline" onClick={() => onEdit(e)} className="h-8 text-[11px] mr-1" data-testid={`de-edit-${e.id}`}>Edit</Button>}
                {onMarkPaid && e.status !== "Paid" && <Button size="sm" onClick={() => onMarkPaid(e)} className="h-8 text-[11px] bg-emerald-600 text-white mr-1" data-testid={`de-paid-${e.id}`}><CheckCircle2 size={11} className="mr-1" /> Mark Paid</Button>}
                {onMarkPending && e.status !== "Pending" && <Button size="sm" onClick={() => onMarkPending(e)} className="h-8 text-[11px] bg-orange-500 text-white mr-1" data-testid={`de-pending-${e.id}`}>Mark Pending</Button>}
                {onResubmit && e.status === "RejectedByApprover" && <Button size="sm" onClick={() => onResubmit(e)} className="h-8 text-[11px] bg-sky-600 hover:bg-sky-700 text-white mr-1" data-testid={`de-resubmit-${e.id}`}>Resubmit</Button>}
                {onDelete && <Button size="sm" variant="outline" onClick={() => onDelete(e)} className="h-8 text-[11px] text-rose-600 border-rose-200 hover:bg-rose-50" data-testid={`de-del-${e.id}`}><Trash size={11} /></Button>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
const Th = ({ children, className = "" }) => <th className={`px-4 py-3 text-left pk-table-header ${className}`}>{children}</th>;
const F = ({ label, children, full }) => (
  <div className={full ? "md:col-span-2" : ""}>
    <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">{label}</Label>
    <div className="mt-1.5">{children}</div>
  </div>
);
const KV = ({ k, v, full }) => <div className={full ? "col-span-2" : ""}><div className="text-[10px] uppercase text-slate-500 font-semibold">{k}</div><div className="text-slate-800 break-words mt-0.5">{v || "—"}</div></div>;

function CreateDialog({ open, setOpen, form, setForm, submit, editing, categories, paymentModes, people, user, billFile, setBillFile, inventory, vendors }) {
  const [newCat, setNewCat] = useState("");
  const [vendorMode, setVendorMode] = useState("existing");
  const [vendorQuery, setVendorQuery] = useState("");
  const allCats = useMemo(() => {
    const merged = [...categories];
    (form.categories || []).forEach((c) => { if (!merged.includes(c)) merged.push(c); });
    return merged;
  }, [categories, form.categories]);
  const isPurchase = (form.categories || []).includes("Purchase");
  const vendorOptions = useMemo(() => {
    const q = vendorQuery.toLowerCase().trim();
    if (!q) return (vendors || []).slice(0, 8);
    return (vendors || []).filter((v) => (v.vendor_name || "").toLowerCase().includes(q)).slice(0, 8);
  }, [vendors, vendorQuery]);
  const purchaseTotal = useMemo(() => {
    return (form.purchase_lines || []).reduce((sum, l) => sum + (Number(l.qty_purchased || 0) * Number(l.purchase_price || 0)), 0);
  }, [form.purchase_lines]);
  const toggleCat = (c) => {
    const current = new Set(form.categories || []);
    if (current.has(c)) { current.delete(c); } else { current.add(c); }
    setForm({ ...form, categories: Array.from(current) });
  };
  const addCustomCat = () => {
    const c = newCat.trim();
    if (!c) return;
    const cs = new Set(form.categories || []);
    cs.add(c);
    setForm({ ...form, categories: Array.from(cs) });
    setNewCat("");
  };
  const onPickPayer = (id) => {
    if (!id) { setForm({ ...form, paid_by_employee_id: "", paid_by_employee_name: "" }); return; }
    if (id === "__self__") { setForm({ ...form, paid_by_employee_id: user?.id || "", paid_by_employee_name: user?.name || "" }); return; }
    const p = (people || []).find((x) => x.id === id);
    setForm({ ...form, paid_by_employee_id: id, paid_by_employee_name: p?.name || "" });
  };
  // Purchase line helpers
  const addLine = () => setForm({ ...form, purchase_lines: [...(form.purchase_lines || []), { ...EMPTY_PURCHASE_LINE }] });
  const updateLine = (idx, patch) => setForm({
    ...form,
    purchase_lines: (form.purchase_lines || []).map((l, i) => i === idx ? { ...l, ...patch } : l),
  });
  const removeLine = (idx) => setForm({
    ...form,
    purchase_lines: (form.purchase_lines || []).filter((_, i) => i !== idx),
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="de-create-dialog">
        <DialogHeader>
          <DialogTitle className="heading">{editing ? "Edit" : "New"} Expense</DialogTitle>
        </DialogHeader>

        <div className="border border-slate-200 rounded-md p-3 bg-slate-50/40 text-[12px]" data-testid="de-filled-by">
          <div className="text-[10.5px] uppercase tracking-wider font-semibold text-violet-700 mb-1.5">Submitted By</div>
          <div className="grid grid-cols-2 gap-y-1 gap-x-3">
            <div><b>Employee ID:</b> {user?.employee_code || "—"}</div>
            <div><b>Employee Name:</b> {user?.name || "—"}</div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 mt-3">
          <F label="Expense Date *"><Input type="date" value={form.expense_date} onChange={(e) => setForm({ ...form, expense_date: e.target.value })} data-testid="de-f-date" /></F>
          <F label={isPurchase ? "Amount (₹) — auto from Purchase total" : "Amount (₹) *"}>
            <Input type="number" value={isPurchase ? purchaseTotal : form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} disabled={isPurchase} className={`mono ${isPurchase ? "bg-slate-50" : ""}`} data-testid="de-f-amount" />
          </F>

          <F label="Category * (pick one or more)" full>
            <div className="flex flex-wrap gap-1.5" data-testid="de-f-categories">
              {allCats.map((c) => {
                const active = (form.categories || []).includes(c);
                return (
                  <button
                    key={c}
                    type="button"
                    onClick={() => toggleCat(c)}
                    className={`text-[12px] px-2.5 py-1 rounded-md border transition ${active ? "bg-violet-600 text-white border-violet-600" : "bg-white text-slate-700 border-slate-300 hover:border-violet-400"}`}
                    data-testid={`de-cat-${c.replace(/\s+/g, "-").toLowerCase()}`}
                  >
                    {active ? "✓ " : ""}{c}
                  </button>
                );
              })}
            </div>
            <div className="flex gap-2 mt-2">
              <Input value={newCat} onChange={(e) => setNewCat(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCustomCat(); } }} placeholder="+ Add new category" className="h-9 text-[12px] flex-1" data-testid="de-f-cat-new" />
              <Button type="button" onClick={addCustomCat} className="h-9 text-[11.5px] bg-violet-600 hover:bg-violet-700 text-white" data-testid="de-f-cat-add">Add</Button>
            </div>
          </F>

          <F label="Breakup of Expense *" full><Textarea rows={3} value={form.purpose} onChange={(e) => setForm({ ...form, purpose: e.target.value })} placeholder="Line-by-line breakup of how the amount was spent" data-testid="de-f-purpose" /></F>
          <F label="Remark" full><Textarea rows={2} value={form.remark} onChange={(e) => setForm({ ...form, remark: e.target.value })} placeholder="Optional notes" data-testid="de-f-remark" /></F>

          <F label="Who Paid for this *">
            <select value={form.paid_by_employee_id || ""} onChange={(e) => onPickPayer(e.target.value)} className="h-10 border border-slate-200 rounded-md px-3 text-sm w-full bg-white" data-testid="de-f-paidby">
              <option value="">—</option>
              {user?.id && <option value="__self__">Myself ({user.name})</option>}
              {(people || []).filter((p) => !p.is_temp_account).map((p) => <option key={p.id} value={p.id}>{p.name} · {p.role}</option>)}
            </select>
          </F>
          <F label="Via *">
            <select value={form.payment_mode_used || ""} onChange={(e) => setForm({ ...form, payment_mode_used: e.target.value })} className="h-10 border border-slate-200 rounded-md px-3 text-sm w-full bg-white" data-testid="de-f-mode">
              <option value="">—</option>
              {(paymentModes || []).map((m) => <option key={m}>{m}</option>)}
            </select>
          </F>
        </div>

        {isPurchase && (
          <div className="mt-4 border border-violet-200 rounded-md p-3 bg-violet-50/30 space-y-3" data-testid="de-purchase-block">
            <div className="text-[10.5px] uppercase tracking-wider font-semibold text-violet-700">Purchase (auto-adds items to Inventory)</div>
            <div className="grid grid-cols-2 gap-3">
              <div className="md:col-span-2">
                <Label className="text-[10.5px] uppercase font-semibold text-slate-600">Vendor *</Label>
                <div className="flex items-center gap-2 mt-1.5">
                  <Button type="button" size="sm" variant={vendorMode === "existing" ? "default" : "outline"} onClick={() => setVendorMode("existing")} className="h-8 text-[11px]">Existing</Button>
                  <Button type="button" size="sm" variant={vendorMode === "new" ? "default" : "outline"} onClick={() => setVendorMode("new")} className="h-8 text-[11px]">+ New</Button>
                </div>
                {vendorMode === "existing" ? (
                  <div className="mt-2">
                    <VendorSearchPicker
                      query={vendorQuery}
                      setQuery={setVendorQuery}
                      options={vendorOptions}
                      selectedId={form.purchase_vendor_id}
                      onPick={(v) => { setForm({ ...form, purchase_vendor_id: v.id, purchase_new_vendor_name: "" }); setVendorQuery(v.vendor_name); }}
                    />
                  </div>
                ) : (
                  <Input className="mt-2" value={form.purchase_new_vendor_name} onChange={(e) => setForm({ ...form, purchase_new_vendor_name: e.target.value, purchase_vendor_id: "" })} placeholder="New vendor name" data-testid="de-purchase-newvendor" />
                )}
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-[10.5px] uppercase font-semibold text-slate-600">Purchase Lines * {form.purchase_lines.length > 0 && <span className="ml-1 inline-flex items-center justify-center min-w-[20px] h-[18px] px-1.5 rounded-full bg-violet-100 text-violet-800 text-[10px] mono font-bold">{form.purchase_lines.length}</span>}</Label>
                <Button type="button" variant="outline" size="sm" onClick={addLine} className="h-8 text-[11px]" data-testid="de-purchase-addline"><Plus size={11} className="mr-1" /> Add Line</Button>
              </div>
              {form.purchase_lines.length === 0 ? (
                <div className="p-3 border border-dashed border-slate-200 rounded-md text-center text-[11.5px] text-slate-400">No lines yet. Click &quot;+ Add Line&quot; to start.</div>
              ) : (
                <div className="space-y-2">
                  {form.purchase_lines.map((line, idx) => (
                    <PurchaseLine key={idx} idx={idx} line={line} inventory={inventory} onUpdate={(patch) => updateLine(idx, patch)} onRemove={() => removeLine(idx)} />
                  ))}
                </div>
              )}
            </div>

            <div className="p-2.5 bg-emerald-50 border border-emerald-200 rounded flex items-center justify-between text-[12px]">
              <span className="uppercase tracking-wider font-semibold text-emerald-700 flex items-center gap-1"><Wallet size={11} /> Purchase Total</span>
              <span className="mono font-bold text-emerald-800">₹ {purchaseTotal.toLocaleString("en-IN")}</span>
            </div>
          </div>
        )}

        <div className="mt-4">
          <Label className="text-[10.5px] uppercase tracking-wider font-semibold text-slate-600">Bill / Receipt {isPurchase ? "*" : "(optional)"}</Label>
          {billFile ? (
            <div className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-md p-2.5 text-[12px] mt-1.5" data-testid="de-bill-preview">
              <div className="flex items-center gap-2 truncate"><Paperclip size={12} /> <span className="truncate">{billFile.name}</span><span className="text-[10.5px] text-slate-500">({Math.round(billFile.size / 1024)} KB)</span></div>
              <button type="button" onClick={() => setBillFile(null)} className="text-rose-600 hover:bg-rose-50 rounded h-7 w-7 inline-flex items-center justify-center" data-testid="de-bill-remove"><Trash size={12} /></button>
            </div>
          ) : (
            <label className="flex items-center justify-center border-2 border-dashed border-slate-300 rounded-md p-3 text-[12px] text-slate-500 hover:bg-slate-50 cursor-pointer mt-1.5" data-testid="de-bill-upload">
              <Upload size={13} className="mr-2" /> Upload Bill / Receipt (PDF / image)
              <input type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => setBillFile(e.target.files?.[0] || null)} />
            </label>
          )}
        </div>

        <DialogFooter className="mt-3">
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={submit} className="bg-[var(--pk-accent)] hover:bg-[var(--pk-accent-hover)] text-white" data-testid="de-submit">{editing ? "Save Changes" : "Submit Expense"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ViewDialog({ viewing, setViewing, onChanged }) {
  const [uploading, setUploading] = useState(false);
  if (!viewing) return null;
  const isOwner = true; // Server enforces ACL; we just always show the upload control to owner-side via API rejection
  const canUpload = viewing.status !== "Cash Given";
  const receiptUrl = viewing.receipt_gridfs_id ? `${process.env.REACT_APP_BACKEND_URL}/api/daily-expenses/${viewing.id}/receipt` : null;
  const onPickFile = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    setUploading(true);
    try {
      await api.post(`/daily-expenses/${viewing.id}/receipt`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Receipt uploaded");
      onChanged?.();
      setViewing({ ...viewing, receipt_filename: file.name, receipt_gridfs_id: "pending" });
    } catch (er) { toast.error(formatError(er.response?.data?.detail)); }
    finally { setUploading(false); }
  };
  return (
    <Dialog open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="de-view-dialog">
        <DialogHeader>
          <DialogTitle className="heading flex items-center gap-2">
            Daily Expense · <Badge className={STATUS_COLOR[viewing.status]}>{viewing.status}</Badge>
          </DialogTitle>
          <div className="text-[11px] text-slate-500">{viewing.created_by_name} · {viewing.created_by_department}</div>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3 text-[12.5px]">
          <KV k="Expense Date" v={viewing.expense_date} />
          <KV k="Categories" v={(viewing.categories && viewing.categories.length) ? viewing.categories.join(" · ") : viewing.category} />
          <KV k="Amount (₹)" v={viewing.amount} />
          <KV k="Employee Code" v={viewing.created_by_employee_code} />
          <KV k="Who Paid (Originally)" v={viewing.paid_by_employee_name} />
          <KV k="Paid Via" v={viewing.payment_mode_used} />
          <KV k="Breakup of Expense" v={viewing.purpose} full />
          <KV k="Remark" v={viewing.remark} full />
          {viewing.purchase_no && <KV k="Linked Purchase" v={viewing.purchase_no} full />}

          {viewing.rejection_remark && <div className="col-span-2 border border-rose-200 bg-rose-50 rounded-md p-2.5"><div className="text-[10px] uppercase font-semibold text-rose-700">Rejected by {viewing.rejected_by_name}</div><div>{viewing.rejection_remark}</div></div>}
          {/* v109.9.169 — Approver-facing view of the resubmission trail */}
          {viewing.rejection_reason && (
            <div className="col-span-2 border border-rose-200 bg-rose-50 rounded-md p-2.5" data-testid="de-view-rejection">
              <div className="text-[10px] uppercase font-semibold text-rose-700">Latest rejection reason ({viewing.rejected_by_name})</div>
              <div className="text-[12px]">{viewing.rejection_reason}</div>
              {Array.isArray(viewing.rejection_history) && viewing.rejection_history.length > 1 && (
                <details className="mt-1.5">
                  <summary className="text-[10.5px] text-rose-700 cursor-pointer">All rejections ({viewing.rejection_history.length})</summary>
                  <ul className="mt-1 space-y-1 text-[11.5px] text-rose-800">
                    {viewing.rejection_history.map((h, i) => (
                      <li key={i}>· <b>{h.by_name}</b> · {(h.at || "").replace("T", " ").slice(0, 16)} — {h.reason}</li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          )}
          {Array.isArray(viewing.clarifications) && viewing.clarifications.length > 0 && (
            <div className="col-span-2 border border-sky-200 bg-sky-50 rounded-md p-2.5" data-testid="de-view-clarifications">
              <div className="text-[10px] uppercase font-semibold text-sky-700 mb-1">Submitter clarifications ({viewing.clarifications.length})</div>
              <ul className="space-y-1.5 text-[12px] text-sky-900">
                {viewing.clarifications.map((c, i) => (
                  <li key={i} className="flex items-start gap-1.5">
                    <span className="mt-0.5 inline-flex h-4 w-4 items-center justify-center rounded-full bg-sky-600 text-white text-[9px] font-bold">{i + 1}</span>
                    <div>
                      <div className="text-[10.5px] text-sky-700">{c.by_name} · {(c.at || "").replace("T", " ").slice(0, 16)}</div>
                      <div>{c.text}</div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {viewing.approval_remark && <div className="col-span-2 border border-emerald-200 bg-emerald-50 rounded-md p-2.5"><div className="text-[10px] uppercase font-semibold text-emerald-700">Approved by {viewing.approved_by_name}</div><div>{viewing.approval_remark}</div></div>}
        </div>

        {viewing.status === "Paid" && (
          <>
            <div className="text-[10.5px] uppercase tracking-wider font-semibold text-emerald-700 mt-3 mb-1">Reimbursement Payment</div>
            <div className="grid grid-cols-2 gap-3 text-[12.5px]">
              <KV k="Payment Mode" v={viewing.payment_mode} />
              <KV k="Reference Number" v={viewing.reference_number} />
              <KV k="Paid Amount (₹)" v={viewing.paid_amount} />
              <KV k="Paid By" v={viewing.cash_given_by_name} />
              <KV k="Paid At" v={viewing.cash_given_at ? viewing.cash_given_at.slice(0, 10) : ""} />
              <KV k="Payment Remark" v={viewing.cash_remark} full />
            </div>
          </>
        )}
        {viewing.status === "Pending" && viewing.pending_remark && (
          <div className="border border-orange-200 bg-orange-50 rounded-md p-2.5 mt-3 text-[12px]">
            <div className="text-[10px] uppercase font-semibold text-orange-700">Pending — {viewing.pending_by_name || ""}</div>
            <div>{viewing.pending_remark}</div>
          </div>
        )}

        <div className="border-t pt-3 mt-3">
          <div className="text-[10.5px] uppercase font-semibold text-violet-700 mb-1.5">Receipt / Bill</div>
          {viewing.receipt_filename ? (
            <div className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-md p-2.5 text-[12px]">
              <div className="flex items-center gap-2 truncate"><Paperclip size={12} /> <span className="truncate">{viewing.receipt_filename}</span></div>
              <div className="flex items-center gap-2">
                {receiptUrl && <a href={receiptUrl} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline text-[11.5px] flex items-center gap-1" data-testid="de-r-download"><Download size={11} /> Open</a>}
                {canUpload && <label className="text-[11.5px] text-violet-700 hover:underline flex items-center gap-1 cursor-pointer" data-testid="de-r-replace"><Upload size={11} /> Replace<input type="file" className="hidden" onChange={(e) => onPickFile(e.target.files?.[0])} /></label>}
              </div>
            </div>
          ) : (
            canUpload ? (
              <label className="flex items-center justify-center border-2 border-dashed border-slate-300 rounded-md p-4 text-[12px] text-slate-500 hover:bg-slate-50 cursor-pointer" data-testid="de-r-upload">
                <Upload size={13} className="mr-2" /> {uploading ? "Uploading…" : "Upload Receipt / Bill"}
                <input type="file" className="hidden" onChange={(e) => onPickFile(e.target.files?.[0])} />
              </label>
            ) : <div className="text-[11.5px] text-slate-400">No receipt uploaded.</div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setViewing(null)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// v109.9.40 — TabAccessDialog and its "Tab Access" trigger button were
// removed per user request. Backend endpoint `/daily-expenses/tab-access`
// remains untouched. Preserved a Reimbursement label mirror below.
function CashGivenTable({ rows, canAdd, currentUserId, isMaster, onAdd, onChanged }) {
  const [pendingDelete, setPendingDelete] = useState(null);
  const confirmDelete = async (reason) => {
    try {
      await api.delete(`/cash-disbursements/${pendingDelete.id}`, { data: { reason } });
      toast.success("Disbursement moved to Recycle Bin");
      onChanged?.();
    } catch (er) { toast.error(formatError(er.response?.data?.detail)); throw er; }
  };
  const onDelete = (d) => setPendingDelete(d);
  return (
    <div className="space-y-3">
      <DeleteWithReasonDialog
        open={!!pendingDelete}
        onClose={() => setPendingDelete(null)}
        title={pendingDelete ? `Delete disbursement to ${pendingDelete.employee_name}?` : "Delete disbursement?"}
        description={pendingDelete ? `₹${pendingDelete.amount} paid via ${pendingDelete.payment_mode} will move to Recycle Bin. Master Admin can restore it.` : ""}
        onConfirm={confirmDelete}
        testid="de-cash-delete"
      />
      <div className="flex items-center justify-end">
        {canAdd && <Button onClick={onAdd} className="bg-blue-600 hover:bg-blue-700 text-white h-9 text-[12px]" data-testid="de-cash-add"><Plus size={13} className="mr-1.5" /> Add Cash Given</Button>}
      </div>
      <div className="pk-card overflow-x-auto" data-testid="de-cash-table">
        <table className="w-full text-sm responsive-table">
          <thead className="border-b border-slate-200 bg-slate-50/60">
            <tr>
              <Th>Employee</Th><Th>Given On</Th><Th>Amount</Th><Th>Mode / Reference</Th><Th>Purpose</Th><Th>Given By</Th>{isMaster && <Th className="text-right">Action</Th>}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={isMaster ? 7 : 6} className="text-center py-12 text-slate-400 text-sm">No cash entries yet.</td></tr>}
            {rows.map((d) => (
              <tr key={d.id} className={`border-b border-slate-100 ${d.employee_id === currentUserId ? "bg-emerald-50/40" : ""}`} data-testid={`de-cash-row-${d.id}`}>
                <td className="px-4 py-3" data-label="Employee">
                  <div className="font-semibold">{d.employee_name}</div>
                  <div className="text-[10.5px] text-slate-500">{d.employee_code || d.employee_department}</div>
                </td>
                <td className="px-4 py-3 mono text-[12px]" data-label="Given On">{d.given_at?.slice(0, 10)}</td>
                <td className="px-4 py-3 mono text-[12.5px] font-semibold text-emerald-700" data-label="Amount">₹{d.amount}</td>
                <td className="px-4 py-3 text-[12px]" data-label="Mode">
                  <div className="font-semibold">{d.payment_mode}</div>
                  {d.reference_number && <div className="text-[10.5px] text-slate-500 mono">{d.reference_number}</div>}
                </td>
                <td className="px-4 py-3 text-[12px]" data-label="Purpose">
                  <div>{d.purpose}</div>
                  {d.remark && <div className="text-[10.5px] text-slate-500 mt-0.5">{d.remark}</div>}
                </td>
                <td className="px-4 py-3 text-[12px]" data-label="Given By">{d.given_by_name}</td>
                {isMaster && (
                  <td className="px-4 py-3 text-right" data-label="Action">
                    <Button size="sm" variant="outline" onClick={() => onDelete(d)} className="h-8 text-[11px] text-rose-600 border-rose-200 hover:bg-rose-50" data-testid={`de-cash-del-${d.id}`}><Trash size={11} /></Button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CashAddDialog({ open, setOpen, people, paymentModes, externalNames, onSaved }) {
  const [form, setForm] = useState({ mode_pick: "existing", employee_id: "", employee_name: "", amount: "", payment_mode: "Cash", reference_number: "", purpose: "Daily Expense", remark: "", given_at: new Date().toISOString().slice(0, 10) });
  useEffect(() => { if (open) setForm({ mode_pick: "existing", employee_id: "", employee_name: "", amount: "", payment_mode: "Cash", reference_number: "", purpose: "Daily Expense", remark: "", given_at: new Date().toISOString().slice(0, 10) }); }, [open]);
  const isCash = (form.payment_mode || "").toLowerCase() === "cash";
  const save = async () => {
    if (form.mode_pick === "existing" && !form.employee_id) { toast.error("Pick an employee"); return; }
    if (form.mode_pick === "freeform" && !(form.employee_name || "").trim()) { toast.error("Type an employee name"); return; }
    if (!form.amount || Number(form.amount) <= 0) { toast.error("Amount must be > 0"); return; }
    if (!isCash && !(form.reference_number || "").trim()) { toast.error("Reference number is required for non-cash"); return; }
    try {
      const payload = {
        employee_id: form.mode_pick === "existing" ? form.employee_id : "",
        employee_name: form.mode_pick === "freeform" ? form.employee_name.trim() : "",
        amount: Number(form.amount), payment_mode: form.payment_mode,
        reference_number: form.reference_number, purpose: form.purpose,
        remark: form.remark, given_at: form.given_at,
      };
      await api.post("/cash-disbursements", payload);
      toast.success("Cash entry recorded");
      setOpen(false); onSaved?.();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-lg" data-testid="de-cash-dialog">
        <DialogHeader><DialogTitle className="heading">Add Cash Given</DialogTitle></DialogHeader>
        <div className="flex items-center gap-2 mb-2">
          <Label className="text-[10.5px] uppercase font-semibold text-slate-600 mr-2">Employee</Label>
          <Button type="button" size="sm" variant={form.mode_pick === "existing" ? "default" : "outline"} onClick={() => setForm({ ...form, mode_pick: "existing", employee_name: "" })} className="h-8 text-[11px]" data-testid="de-cash-mode-existing">Existing</Button>
          <Button type="button" size="sm" variant={form.mode_pick === "freeform" ? "default" : "outline"} onClick={() => setForm({ ...form, mode_pick: "freeform", employee_id: "" })} className="h-8 text-[11px]" data-testid="de-cash-mode-freeform">Type Name</Button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          {form.mode_pick === "existing" ? (
            <F label="Pick Employee *" full>
              <select value={form.employee_id} onChange={(e) => setForm({ ...form, employee_id: e.target.value })} className="h-10 border border-slate-200 rounded-md px-3 text-sm w-full bg-white" data-testid="de-cash-emp">
                <option value="">—</option>
                {(people || []).filter((p) => !p.is_temp_account).map((p) => <option key={p.id} value={p.id}>{p.name} · {p.role}</option>)}
              </select>
            </F>
          ) : (
            <F label="Employee Name *" full>
              <Input
                value={form.employee_name}
                onChange={(e) => setForm({ ...form, employee_name: e.target.value })}
                placeholder="Type the recipient's name"
                list="de-cash-external-names"
                autoComplete="off"
                data-testid="de-cash-emp-freeform"
              />
              <datalist id="de-cash-external-names">
                {(externalNames || []).map((n) => <option key={n} value={n} />)}
              </datalist>
              <div className="text-[10px] text-slate-500 mt-1">
                Use this for ad-hoc / external recipients who aren&apos;t in the directory yet.
                {externalNames && externalNames.length > 0 && (
                  <span className="ml-1">Previously used names auto-suggest as you type.</span>
                )}
              </div>
            </F>
          )}
          <F label="Given On *"><Input type="date" value={form.given_at} onChange={(e) => setForm({ ...form, given_at: e.target.value })} data-testid="de-cash-date" /></F>
          <F label="Amount (₹) *"><Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="mono" data-testid="de-cash-amt" /></F>
          <F label="Mode *">
            <select value={form.payment_mode} onChange={(e) => setForm({ ...form, payment_mode: e.target.value })} className="h-10 border border-slate-200 rounded-md px-3 text-sm w-full bg-white" data-testid="de-cash-mode">
              {(paymentModes || []).map((m) => <option key={m}>{m}</option>)}
            </select>
          </F>
          {!isCash && (
            <F label="Reference Number *" full><Input value={form.reference_number} onChange={(e) => setForm({ ...form, reference_number: e.target.value })} className="mono" data-testid="de-cash-ref" /></F>
          )}
          <F label="Purpose" full><Input value={form.purpose} onChange={(e) => setForm({ ...form, purpose: e.target.value })} data-testid="de-cash-purpose" /></F>
          <F label="Remark" full><Textarea rows={2} value={form.remark} onChange={(e) => setForm({ ...form, remark: e.target.value })} data-testid="de-cash-remark" /></F>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={save} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="de-cash-save">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReimbPayDialog({ viewing, setViewing, paymentModes, onSaved }) {
  const [form, setForm] = useState({ payment_mode: "UPI", reference_number: "", paid_amount: "", remark: "" });
  useEffect(() => {
    if (viewing) setForm({ payment_mode: "UPI", reference_number: "", paid_amount: viewing.amount, remark: "" });
  }, [viewing]);
  if (!viewing) return null;
  const isCash = (form.payment_mode || "").toLowerCase() === "cash";
  const save = async () => {
    if (!form.payment_mode) { toast.error("Pick a mode"); return; }
    if (!isCash && !(form.reference_number || "").trim()) { toast.error("Reference number required for non-cash"); return; }
    try {
      await api.post(`/daily-expenses/${viewing.id}/reimbursement-status`, { action: "paid", ...form });
      toast.success("Marked Paid");
      setViewing(null); onSaved?.();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
      <DialogContent className="max-w-md" data-testid="de-reimb-pay-dialog">
        <DialogHeader><DialogTitle className="heading">Mark Reimbursement Paid</DialogTitle></DialogHeader>
        <div className="text-[12px] text-slate-500 mb-2">{viewing.created_by_name} · ₹{viewing.amount} · {viewing.expense_date}</div>
        <div className="grid grid-cols-2 gap-3">
          <F label="Payment Mode *">
            <select value={form.payment_mode} onChange={(e) => setForm({ ...form, payment_mode: e.target.value })} className="h-10 border border-slate-200 rounded-md px-3 text-sm w-full bg-white" data-testid="de-reimb-mode">
              {(paymentModes || []).map((m) => <option key={m}>{m}</option>)}
            </select>
          </F>
          <F label="Amount Paid (₹) *"><Input type="number" value={form.paid_amount} onChange={(e) => setForm({ ...form, paid_amount: e.target.value })} className="mono" data-testid="de-reimb-amt" /></F>
          {!isCash && <F label="Reference Number *" full><Input value={form.reference_number} onChange={(e) => setForm({ ...form, reference_number: e.target.value })} className="mono" data-testid="de-reimb-ref" /></F>}
          <F label="Remark" full><Textarea rows={2} value={form.remark} onChange={(e) => setForm({ ...form, remark: e.target.value })} data-testid="de-reimb-remark" /></F>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setViewing(null)}>Cancel</Button>
          <Button onClick={save} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="de-reimb-save">Mark Paid</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function CashBookTab({ summary, onOpen }) {
  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    if (!q) return summary;
    const t = q.toLowerCase();
    return summary.filter((r) => (r.employee_name || "").toLowerCase().includes(t) || (r.employee_code || "").toLowerCase().includes(t) || (r.department || "").toLowerCase().includes(t));
  }, [summary, q]);
  return (
    <div className="space-y-3" data-testid="de-cashbook-tab">
      <div className="relative max-w-md">
        <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search employee, code, department…" className="pl-7 h-9 text-[12px]" data-testid="de-cashbook-search" />
      </div>
      <div className="pk-card overflow-x-auto">
        <table className="w-full text-sm responsive-table">
          <thead className="border-b border-slate-200 bg-slate-50/60">
            <tr>
              <Th>Employee</Th><Th>Department</Th><Th className="text-right">Credits (Cash Given)</Th><Th className="text-right">Debits (Expenses)</Th><Th className="text-right">Balance (In Hand)</Th><Th>Entries</Th><Th className="text-right">Ledger</Th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan={7} className="text-center py-12 text-slate-400 text-sm">No ledger entries yet.</td></tr>}
            {filtered.map((r) => (
              <tr key={`${r.employee_id || ""}-${r.employee_name}`} className="border-b border-slate-100" data-testid={`de-cashbook-row-${r.employee_id || r.employee_name.replace(/\s/g, '-')}`}>
                <td className="px-4 py-3" data-label="Employee">
                  <div className="font-semibold flex items-center gap-1.5">{r.employee_name}{r.is_external && <Badge variant="outline" className="text-[9.5px] bg-amber-50 border-amber-200 text-amber-700">External</Badge>}</div>
                  <div className="text-[10.5px] text-slate-500">{r.employee_code || "—"}</div>
                </td>
                <td className="px-4 py-3 text-[12px]" data-label="Department">{r.department || "—"}</td>
                <td className="px-4 py-3 mono text-right text-emerald-700 font-semibold" data-label="Credits">₹{r.credit_total?.toLocaleString("en-IN")}</td>
                <td className="px-4 py-3 mono text-right text-rose-700" data-label="Debits">₹{r.debit_total?.toLocaleString("en-IN")}</td>
                <td className={`px-4 py-3 mono text-right font-bold ${r.balance < 0 ? "text-rose-700" : "text-slate-800"}`} data-label="Balance">₹{r.balance?.toLocaleString("en-IN")}</td>
                <td className="px-4 py-3 mono text-[12px]" data-label="Entries">{r.entries}</td>
                <td className="px-4 py-3 text-right" data-label="Ledger">
                  <Button size="sm" variant="outline" onClick={() => onOpen(r)} className="h-8 text-[11px]" data-testid={`de-cashbook-open-${r.employee_id || r.employee_name.replace(/\s/g, '-')}`}>View Ledger</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CashBookDialog({ detail, onClose, canAddDebit, paymentModes, onDebitAdded }) {
  const [debitOpen, setDebitOpen] = useState(false);
  const [debit, setDebit] = useState({ amount: "", payment_mode: "Cash", reference_number: "", purpose: "", remark: "", given_at: new Date().toISOString().slice(0, 10) });
  useEffect(() => {
    if (debitOpen) setDebit({ amount: "", payment_mode: "Cash", reference_number: "", purpose: "", remark: "", given_at: new Date().toISOString().slice(0, 10) });
  }, [debitOpen]);
  if (!detail) return null;
  const isExternal = !!detail.is_external;
  const showAddDebit = canAddDebit && isExternal;
  const isCashMode = (debit.payment_mode || "").toLowerCase() === "cash";
  const saveDebit = async () => {
    if (!debit.amount || Number(debit.amount) <= 0) { toast.error("Amount must be > 0"); return; }
    if (!isCashMode && !(debit.reference_number || "").trim()) { toast.error("Reference number required for non-cash"); return; }
    try {
      await api.post("/cash-disbursements/external-debit", {
        employee_name: detail.employee_name,
        amount: Number(debit.amount),
        payment_mode: debit.payment_mode,
        reference_number: debit.reference_number,
        purpose: debit.purpose || "External Debit",
        remark: debit.remark,
        given_at: debit.given_at,
      });
      toast.success("Debit entry added");
      setDebitOpen(false);
      onDebitAdded?.();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={!!detail} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="de-cashbook-dialog">
        <DialogHeader>
          <div className="flex items-start justify-between gap-2">
            <div>
              <DialogTitle className="heading">Cash Book · {detail.employee_name}{detail.is_external && " (External)"}</DialogTitle>
              <div className="text-[11px] text-slate-500">{detail.employee_code || "—"}{detail.department ? ` · ${detail.department}` : ""}</div>
            </div>
            {showAddDebit && (
              <Button
                size="sm"
                onClick={() => setDebitOpen((v) => !v)}
                className="h-8 px-3 text-[11.5px] bg-rose-600 hover:bg-rose-700 text-white shrink-0"
                data-testid="de-cashbook-add-debit-btn"
              >
                {debitOpen ? "Cancel" : "+ Add Debit"}
              </Button>
            )}
          </div>
        </DialogHeader>

        {showAddDebit && debitOpen && (
          <div className="border border-rose-200 bg-rose-50/60 rounded-md p-3 mb-3" data-testid="de-cashbook-add-debit-form">
            <div className="text-[11px] uppercase tracking-wider text-rose-700 font-bold mb-2">Record Debit for {detail.employee_name}</div>
            <div className="grid grid-cols-2 gap-2">
              <F label="Date *"><Input type="date" value={debit.given_at} onChange={(e) => setDebit({ ...debit, given_at: e.target.value })} data-testid="de-cashbook-debit-date" /></F>
              <F label="Amount (₹) *"><Input type="number" value={debit.amount} onChange={(e) => setDebit({ ...debit, amount: e.target.value })} className="mono" data-testid="de-cashbook-debit-amt" /></F>
              <F label="Mode *">
                <select value={debit.payment_mode} onChange={(e) => setDebit({ ...debit, payment_mode: e.target.value })} className="h-10 border border-slate-200 rounded-md px-3 text-sm w-full bg-white" data-testid="de-cashbook-debit-mode">
                  {(paymentModes || ["Cash", "UPI", "NEFT", "RTGS"]).map((m) => <option key={m}>{m}</option>)}
                </select>
              </F>
              {!isCashMode && (
                <F label="Reference Number *"><Input value={debit.reference_number} onChange={(e) => setDebit({ ...debit, reference_number: e.target.value })} className="mono" data-testid="de-cashbook-debit-ref" /></F>
              )}
              <F label="Purpose" full><Input value={debit.purpose} onChange={(e) => setDebit({ ...debit, purpose: e.target.value })} placeholder="e.g. Return · Utilised for X" data-testid="de-cashbook-debit-purpose" /></F>
              <F label="Remark" full><Textarea rows={2} value={debit.remark} onChange={(e) => setDebit({ ...debit, remark: e.target.value })} data-testid="de-cashbook-debit-remark" /></F>
            </div>
            <div className="flex items-center justify-end gap-2 mt-3">
              <Button variant="outline" size="sm" onClick={() => setDebitOpen(false)} className="h-8 text-[11px]">Cancel</Button>
              <Button size="sm" onClick={saveDebit} className="h-8 text-[11px] bg-rose-600 hover:bg-rose-700 text-white" data-testid="de-cashbook-debit-save">Record Debit</Button>
            </div>
          </div>
        )}

        <div className="grid grid-cols-3 gap-2 mb-3">
          <div className="border border-emerald-200 bg-emerald-50 rounded-md p-2.5">
            <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-semibold">Total Credits</div>
            <div className="mono font-bold text-emerald-800 text-[14px]">₹{detail.credit_total?.toLocaleString("en-IN")}</div>
          </div>
          <div className="border border-rose-200 bg-rose-50 rounded-md p-2.5">
            <div className="text-[10px] uppercase tracking-wider text-rose-700 font-semibold">Total Debits</div>
            <div className="mono font-bold text-rose-800 text-[14px]">₹{detail.debit_total?.toLocaleString("en-IN")}</div>
          </div>
          <div className={`border rounded-md p-2.5 ${detail.balance < 0 ? "border-rose-300 bg-rose-50" : "border-slate-200 bg-slate-50"}`}>
            <div className="text-[10px] uppercase tracking-wider text-slate-700 font-semibold">Balance (Cash in Hand)</div>
            <div className={`mono font-bold text-[14px] ${detail.balance < 0 ? "text-rose-800" : "text-slate-800"}`}>₹{detail.balance?.toLocaleString("en-IN")}</div>
          </div>
        </div>

        <div className="overflow-x-auto border rounded-md">
          <table className="w-full text-[12px]">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-3 py-2">Date</th>
                <th className="text-left px-3 py-2">Type</th>
                <th className="text-left px-3 py-2">Mode</th>
                <th className="text-left px-3 py-2">Purpose / Detail</th>
                <th className="text-right px-3 py-2">Credit</th>
                <th className="text-right px-3 py-2">Debit</th>
                <th className="text-right px-3 py-2">Balance</th>
              </tr>
            </thead>
            <tbody>
              {detail.rows.length === 0 && <tr><td colSpan={7} className="text-center py-8 text-slate-400">No entries</td></tr>}
              {detail.rows.map((r) => (
                <tr key={r.ref_id} className="border-t" data-testid={`de-cashbook-entry-${r.ref_id}`}>
                  <td className="px-3 py-2 mono text-[11.5px]">{(r.date || "").slice(0, 10)}</td>
                  <td className="px-3 py-2"><Badge className={r.kind === "credit" ? "bg-emerald-100 text-emerald-800" : "bg-rose-100 text-rose-700"}>{r.type}</Badge></td>
                  <td className="px-3 py-2">{r.mode || "—"}{r.reference_number ? <div className="text-[10.5px] text-slate-500 mono">{r.reference_number}</div> : null}</td>
                  <td className="px-3 py-2"><div>{r.purpose}</div>{r.remark && <div className="text-[10.5px] text-slate-500">{r.remark}</div>}{r.given_by_name && <div className="text-[10.5px] text-slate-400">by {r.given_by_name}</div>}</td>
                  <td className="px-3 py-2 text-right mono text-emerald-700">{r.kind === "credit" ? `₹${r.amount.toLocaleString("en-IN")}` : ""}</td>
                  <td className="px-3 py-2 text-right mono text-rose-700">{r.kind === "debit" ? `₹${r.amount.toLocaleString("en-IN")}` : ""}</td>
                  <td className={`px-3 py-2 text-right mono font-semibold ${r.running_balance < 0 ? "text-rose-700" : "text-slate-800"}`}>₹{r.running_balance.toLocaleString("en-IN")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// v109.9.168 — Approval queue table (approver-only view).
function ApprovalQueueTable({ rows, currentUserId, onView, onApprove, onReject }) {
  if (!rows || rows.length === 0) {
    return <div className="p-8 text-center text-slate-500 text-[12px]">No pending expenses awaiting your approval.</div>;
  }
  return (
    <div className="rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-x-auto">
      <table className="w-full text-[12px]">
        <thead className="bg-slate-50 dark:bg-slate-800/60">
          <tr className="text-left [&>th]:px-3 [&>th]:py-2 [&>th]:text-[10px] [&>th]:uppercase [&>th]:tracking-wider [&>th]:text-slate-600">
            <th>Submitted By</th><th>Category</th><th>Amount</th><th>Purpose</th><th>Status</th><th>When</th><th className="text-right">Action</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const isOwn = String(r.created_by_id) === String(currentUserId);
            return (
              <tr key={r.id} className="border-t border-slate-100 dark:border-slate-800 [&>td]:px-3 [&>td]:py-2 hover:bg-slate-50/70 dark:hover:bg-slate-900/60" data-testid={`de-appr-row-${r.id}`}>
                <td className="font-semibold">{r.created_by_name}</td>
                <td>{Array.isArray(r.categories) ? r.categories.join(", ") : (r.categories || r.category || "—")}</td>
                <td className="mono font-semibold">₹{Number(r.amount || 0).toLocaleString("en-IN")}</td>
                <td className="text-slate-700 max-w-[280px] truncate" title={r.purpose || ""}>{r.purpose || "—"}</td>
                <td><span className="text-[10.5px] font-semibold uppercase tracking-wider text-amber-700">{r.status}</span></td>
                <td className="text-[10.5px] text-slate-500">{(r.created_at || "").replace("T", " ").slice(0, 16)}</td>
                <td className="text-right">
                  <div className="inline-flex gap-1 items-center">
                    <button onClick={() => onView(r)} className="h-6 px-2 rounded text-[10.5px] border border-slate-200 hover:bg-slate-50" data-testid={`de-appr-view-${r.id}`}>View</button>
                    {!isOwn && (
                      <>
                        <button onClick={() => onApprove(r)} className="h-6 px-2 rounded text-[10.5px] border border-emerald-200 text-emerald-700 hover:bg-emerald-50" data-testid={`de-appr-approve-${r.id}`}>Approve</button>
                        <button onClick={() => onReject(r)} className="h-6 px-2 rounded text-[10.5px] border border-rose-200 text-rose-700 hover:bg-rose-50" data-testid={`de-appr-reject-${r.id}`}>Reject</button>
                      </>
                    )}
                    {isOwn && <span className="text-[10px] italic text-slate-400 px-1">own submission</span>}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
